import {css} from 'styled-components';
import Colr from 'colr';
import common, {spacing} from '../../common';
import {ThemeSchema} from '../../types';

export const hoverColor = (color: string): string =>
  Colr.fromHex(color)
    .darken(10)
    .toHex();
export const activeColor = (color: string): string =>
  Colr.fromHex(color)
    .darken(20)
    .toHex();

export const disabledTextColor = (color: string): string => {
  const rgb = Colr.fromHex(color)
    .toRgbArray()
    .toString();
  return `rgba(${rgb}, .3)`;
};

const typeScale = {
  1: '2.625rem', // 42px
  2: '2.375rem', // 38px
  3: '2rem', // 32px
  4: '1.625rem', // 26px
  5: '1.375rem', // 22px
  6: '1.25rem', // 20px
  7: '1rem', // 16px
  8: '.875rem' // 14px
};

const lineHeights = [1, 1.2, 1.5];

const tracking = {
  normal: 'normal'
};

const fonts = {
  GTWalsheim: '"GT-Walsheim", Arial, sans-serif'
};

const brandLight = '#008c80';

const colors = {
  brand: {
    base: '#477f80',
    light: brandLight,
    dark: '#014345'
  },
  accent: '#ffcd05',
  shades: {
    black: '#000000',
    darkest: '#004346',
    darker: '#b3b3b3',
    dark: '#cdcbc3',
    light: '#dedcd5',
    lighter: '#eef2f2',
    lightest: '#f5f3f1',
    nearWhite: '#fafafa',
    white: '#ffffff'
  },
  focus: 'rgba(255,205,5,0.3)',
  success: '#00928a',
  error: '#bd1903',
  info: '#005bb9',
  product: {
    hospital: brandLight,
    extras: brandLight,
    combined: brandLight,
    highlight: {
      backgroundColor: '#ffcd05',
      textColor: '#000000'
    }
  }
};

const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '2px',
  outlineOffset: '0.125em'
};

const baseButtonStyles = css`
  box-sizing: border-box;
  display: inline-block;
  min-width: 2.7em;
  vertical-align: middle;
  font-weight: 400;
  border-width: 1px;
  border-style: solid;
  color: ${colors.shades.white};
  text-align: center;
  text-decoration: none;
  text-shadow: none;
  background-color: ${colors.brand.base};
  border-radius: 100px;
  outline: 0;
  cursor: pointer;
  user-select: none;
  font-family: ${fonts.GTWalsheim};
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  transition-property: background, border-color, color;

  &:hover {
  }

  &:active {
  }

  &:disabled {
    background-color: rgba(71, 128, 129, 0.2);
    border-color: #111e1e;
    color: #478081;
    opacity: 0.5;
    cursor: no-drop;
  }

  &:focus {
    outline-width: ${focus.outlineWidth};
    outline-style: ${focus.outlineStyle};
    outline-color: ${focus.outlineColor};
    outline-offset: ${focus.outlineOffset};
  }
`;

const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles}
      background-color: ${colors.accent};
      border-color: ${colors.accent};
      color: ${colors.shades.black};
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);

      &:hover {
        background-color: #ffe784;
      }
      
      &:active {
        box-shadow: inset 3.5px 3.5px #ebbc00;
      }

      &:disabled {
        opacity: 1;
        background-color: rgba(255,205,5,0.2);
        border: dashed 1px #ffcd05;
        color: #ffcd05;
      }
    `,
    selected: css`
      background-color: #ebbc00;
    `,
    disabled: css`
      opacity: 0.3;
      background-color: rgba(255, 205, 5, 0.2);
      border: dashed 1px #ffcd05;
      color: #000000;
    `
  },
  secondary: {
    normal: css`
      ${baseButtonStyles};
      color: ${colors.brand.light};
      background-color: #fff;
      border: 1px solid #478081;

      &:hover {
        color: ${colors.brand.light};
        background-color: #e6f4f2;
      }

      &:active {
        background-color: #cce8e6;
        box-shadow: inset 0 2px 4px 0 ${colors.brand.light};
      }

      &:disabled {
        background-color: rgba(71, 128, 129, 0.2);
        border: dashed 1px #478081;
        color: #478081;
      }
    `,
    selected: css`
      background-color: #dae6e6;
    `,
    disabled: css`
      background-color: rgba(71, 128, 129, 0.2);
      border: dashed 1px #478081;
      color: #478081;
    `
  },
  small: css`
    padding: ${spacing[2]} ${spacing[3]};
    font-size: ${typeScale[8]};
    line-height: ${lineHeights[0]};
  `,
  medium: css`
    padding: ${spacing[3]} ${spacing[5]};
    font-size: ${typeScale[7]};
    line-height: ${lineHeights[1]};
  `,
  large: css`
    padding: ${spacing[3]} ${spacing[5]};
    font-size: ${typeScale[5]};
    line-height: ${lineHeights[1]};
  `
};

const baseFormValues = {
  borderColor: colors.shades.darker,
  borderColorValid: colors.success,
  borderColorInvalid: colors.error,
  helpTextColor: colors.shades.darkest,
  helpTextSize: typeScale[8],
  labelColor: colors.shades.black,
  labelSize: typeScale[7],
  selectedToggleColor: colors.brand.light,
  errorMessageTextColor: colors.error,
  infoMessageTextColor: colors.info
};

const suncorp: ThemeSchema = {
  ...common,
  typeScale,
  fonts,
  colors,
  focus,

  id: 'suncorp',
  mode: {
    form: 'light' //default
  },

  headings: {
    color: 'currentColor',
    h1: {
      fontFamily: fonts.GTWalsheim,
      fontSize: typeScale[1],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.GTWalsheim,
      fontSize: typeScale[2],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.GTWalsheim,
      fontSize: typeScale[3],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.GTWalsheim,
      fontSize: typeScale[4],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.GTWalsheim,
      fontSize: typeScale[5],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h6: {
      fontFamily: fonts.GTWalsheim,
      fontSize: typeScale[6],
      lineHeight: lineHeights[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    }
  },

  copy: {
    color: colors.shades.black,
    fontFamily: fonts.GTWalsheim,
    fontSize: typeScale[7],
    fontWeight: 400,
    lineHeight: lineHeights[2],
    smallFontSize: typeScale[8],
    largeFontSize: typeScale[5]
  },

  link: {
    loudColor: colors.brand.light,
    darkColor: colors.shades.black,
    lightColor: colors.shades.white
  },

  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: {
      dark: buttonStyles.primary,
      light: buttonStyles.primary
    },
    secondary: {
      dark: buttonStyles.secondary,
      light: buttonStyles.secondary
    }
  },

  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `0 4px 8px 0 ${colors.shades.lighter}`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  },

  smallFooter: {
    backgroundColor: colors.shades.white,
    textColor: colors.shades.darkest,
    linkColor: colors.brand.light
  },

  panel: {
    loud: {
      backgroundColor: colors.brand.base,
      textColor: colors.shades.white
    },
    muted: {
      backgroundColor: colors.shades.lighter,
      textColor: colors.shades.darkest
    },
    light: {
      backgroundColor: colors.shades.lightest,
      textColor: colors.shades.darkest
    },
    dark: {
      backgroundColor: colors.shades.darkest,
      textColor: colors.shades.white
    },
    white: {
      backgroundColor: colors.shades.white,
      textColor: colors.shades.darkest
    }
  }
};

export default suncorp;
